import React, { useState, useEffect } from 'react';
import { 
  Wallet, TrendingUp, Clock, CheckCircle, Euro,
  ArrowRight, Calendar, Package
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { earningsApi } from '../../lib/api';
import { formatDate, formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

const CarrierEarningsPage = () => {
  const [earnings, setEarnings] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [earningsRes, payoutsRes] = await Promise.all([
        earningsApi.getEarnings(),
        earningsApi.getPayouts()
      ]);
      setEarnings(earningsRes.data);
      setPayouts(payoutsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des gains');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      processing: 'En cours',
      completed: 'Versé',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      processing: 'bg-blue-100 text-blue-700 border-blue-200',
      completed: 'bg-green-100 text-green-700 border-green-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="spinner mx-auto"></div>
        <p className="text-slate-500 mt-4">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="carrier-earnings-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Mes gains
        </h1>
        <p className="text-slate-600 mt-1">Suivez vos revenus et versements</p>
      </div>

      {/* Earnings Summary Cards */}
      {earnings && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">En attente</p>
                  <p className="text-3xl font-bold text-amber-700 mt-1">
                    {formatPrice(earnings.pending_amount)}
                  </p>
                  <p className="text-xs text-amber-500 mt-1">
                    {earnings.pending_count} livraison(s)
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Versé</p>
                  <p className="text-3xl font-bold text-green-700 mt-1">
                    {formatPrice(earnings.completed_amount)}
                  </p>
                  <p className="text-xs text-green-500 mt-1">
                    {earnings.completed_count} versement(s)
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Ce mois</p>
                  <p className="text-3xl font-bold text-blue-700 mt-1">
                    {formatPrice(earnings.month_earnings)}
                  </p>
                  <p className="text-xs text-blue-500 mt-1">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Total gagné</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">
                    {formatPrice(earnings.total_earnings)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Depuis votre inscription
                  </p>
                </div>
                <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Euro className="w-5 h-5 text-blue-600" />
            <p className="text-sm text-blue-700">
              <strong>Comment ça marche?</strong> Les versements sont effectués manuellement par l&apos;administrateur après chaque livraison confirmée. 
              Vous recevrez un email une fois le versement effectué.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Payouts History */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
            <Wallet className="w-5 h-5 text-orange-500" />
            Historique des versements
          </CardTitle>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">Aucun versement pour le moment</p>
              <p className="text-slate-400 text-sm mt-2">
                Complétez des livraisons pour gagner de l&apos;argent!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {payouts.map((payout) => (
                <div 
                  key={payout.id} 
                  className="flex flex-col md:flex-row md:items-center gap-4 p-4 bg-slate-50 rounded-xl"
                  data-testid={`payout-${payout.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                      <span className="font-medium">{payout.pickup_city}</span>
                      <ArrowRight className="w-4 h-4" />
                      <span className="font-medium">{payout.delivery_city}</span>
                    </div>
                    {payout.parcel_description && (
                      <p className="text-sm text-slate-500 truncate max-w-md">
                        {payout.parcel_description}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      Livré le {formatDate(payout.delivered_at)}
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(payout.status)}`}>
                      {payout.status === 'pending' && <Clock className="w-4 h-4" />}
                      {payout.status === 'completed' && <CheckCircle className="w-4 h-4" />}
                      {getStatusLabel(payout.status)}
                    </span>
                    <p className="text-2xl font-bold text-green-600">
                      {formatPrice(payout.amount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CarrierEarningsPage;
